/**
 * 🪤 Lifeless Portrait — Upkeep Phase prompt (visual-only demo).
 *
 * Visualises the DFCL-334 upkeep decision each turn:
 *   "Pay 1 token or sacrifice your lowest-DEF Fighter?"
 *
 * This is a self-contained preview — not wired to real DFCL match state.
 * A tiny mock scene with Tokens + 3 Fighters lets the owner feel the
 * cadence of the Trap without a full match running.
 *
 * Rules modelled (per #334 catalog entry):
 *   • Pay 1 token  → Portrait holds, token count ticks down.
 *   • Sacrifice    → Lowest-DEF Fighter is auto-selected and destroyed.
 *   • Cannot pay AND no Fighters → Portrait self-destroys (goes to Ash Pile).
 */
import { useMemo, useState } from "react";

const SEED_TOKENS   = 3;
const SEED_FIGHTERS = [
  { name: "Bram",     def: 2200 },
  { name: "Xyla",     def: 2200 },
  { name: "Dio",      def: 1200 },  // lowest DEF — gets sacrificed first
];

function lowestDefIndex(list) {
  if (!list.length) return -1;
  let idx = 0;
  for (let i = 1; i < list.length; i++) {
    if (list[i].def < list[idx].def) idx = i;
  }
  return idx;
}

export default function PortraitUpkeepPrompt({ onClose }) {
  const [turn,     setTurn]     = useState(1);
  const [tokens,   setTokens]   = useState(SEED_TOKENS);
  const [fighters, setFighters] = useState(SEED_FIGHTERS);
  const [ashPile,  setAshPile]  = useState([]);
  const [portraitAlive, setPortraitAlive] = useState(true);
  const [lastLog,  setLastLog]  = useState(
    "Upkeep triggered — Lifeless Portrait demands tribute."
  );

  const lowestIdx = useMemo(() => lowestDefIndex(fighters), [fighters]);
  const lowest    = lowestIdx >= 0 ? fighters[lowestIdx] : null;

  const canPay       = tokens > 0;
  const canSacrifice = fighters.length > 0;
  const canHold      = canPay || canSacrifice;
  const resolved     = !portraitAlive;

  function nextUpkeep() {
    setTurn((t) => t + 1);
    setLastLog("Upkeep triggered — Lifeless Portrait demands tribute.");
  }

  function pay() {
    if (!canPay || resolved) return;
    setTokens((t) => t - 1);
    setLastLog(`Turn ${turn}: Paid 1 token · Lifeless Portrait holds.`);
    setTurn((t) => t + 1);
  }

  function sacrifice() {
    if (!canSacrifice || resolved) return;
    const sac = fighters[lowestIdx];
    const next = fighters.filter((_, i) => i !== lowestIdx);
    setFighters(next);
    setAshPile((a) => [...a, sac]);
    setLastLog(
      `Turn ${turn}: Sacrificed ${sac.name} (DEF ${sac.def.toLocaleString()}) · Lifeless Portrait holds.`
    );
    setTurn((t) => t + 1);
  }

  function autoDestroy() {
    setPortraitAlive(false);
    setLastLog(
      `Turn ${turn}: No tokens, no Fighters · Lifeless Portrait is destroyed.`
    );
  }

  function reset() {
    setTurn(1);
    setTokens(SEED_TOKENS);
    setFighters(SEED_FIGHTERS);
    setAshPile([]);
    setPortraitAlive(true);
    setLastLog("Upkeep triggered — Lifeless Portrait demands tribute.");
  }

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="portrait-upkeep-prompt"
    >
      <div
        className="max-w-md w-full bg-[#0F0A08] border-2 border-[#7c3aed]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-4 py-3 border-b border-zinc-800 bg-gradient-to-r from-[#1a0e22] to-[#0F0A08]">
          <div className="flex items-center gap-2">
            <span className="text-xl">🪤</span>
            <div className="flex-1">
              <div className="text-[9px] uppercase tracking-widest text-[#a78bfa]">
                Portrait Upkeep · Turn {turn}
              </div>
              <div className="font-display text-lg font-black text-white leading-tight">
                Lifeless Portrait
              </div>
            </div>
            <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 border border-neutral-700 text-neutral-500 rounded-sm">
              Visual Preview
            </span>
          </div>
        </div>

        {/* Scene state */}
        <div className="px-4 py-3 grid grid-cols-3 gap-2 text-center text-[11px] border-b border-zinc-900">
          <div data-testid="upkeep-tokens">
            <div className="text-[9px] uppercase tracking-widest text-neutral-500">Tokens</div>
            <div className="text-2xl font-black tabular-nums text-[#FCD34D]">{tokens}</div>
          </div>
          <div data-testid="upkeep-fighters">
            <div className="text-[9px] uppercase tracking-widest text-neutral-500">Fighters</div>
            <div className="text-2xl font-black tabular-nums text-cyan-300">{fighters.length}</div>
          </div>
          <div data-testid="upkeep-portrait">
            <div className="text-[9px] uppercase tracking-widest text-neutral-500">Portrait</div>
            <div className={`text-2xl font-black ${portraitAlive ? "text-[#a78bfa]" : "text-red-500 line-through"}`}>
              {portraitAlive ? "ALIVE" : "DEAD"}
            </div>
          </div>
        </div>

        {/* Fighters list — flags lowest-DEF */}
        <div className="px-4 py-3 border-b border-zinc-900">
          <div className="text-[9px] uppercase tracking-widest text-neutral-500 mb-1">Your Fighters</div>
          {fighters.length === 0 ? (
            <div className="text-[11px] italic text-neutral-500">No Fighters on your side of the field.</div>
          ) : (
            <ul className="space-y-1">
              {fighters.map((f, i) => (
                <li
                  key={i}
                  data-testid={`upkeep-fighter-${f.name.toLowerCase()}`}
                  className={`flex items-center justify-between text-[11px] px-2 py-1 ${
                    i === lowestIdx ? "bg-red-900/30 border border-red-700/60" : "bg-black/40 border border-zinc-800"
                  }`}
                >
                  <span className="text-neutral-200">
                    {i === lowestIdx && !resolved && <span className="text-red-400 mr-1">▶</span>}
                    {f.name}
                  </span>
                  <span className="text-cyan-300 tabular-nums">🛡 {f.def.toLocaleString()}</span>
                </li>
              ))}
            </ul>
          )}
          {!!ashPile.length && (
            <div className="mt-2 text-[10px] text-neutral-500">
              🔥 Ash Pile: {ashPile.map((f) => f.name).join(", ")}
            </div>
          )}
        </div>

        {/* Log line */}
        <div
          className="px-4 py-2 bg-black/40 text-[11px] text-neutral-300 italic border-b border-zinc-900"
          data-testid="upkeep-log"
        >
          {lastLog}
        </div>

        {/* Choice buttons */}
        <div className="p-4 space-y-2">
          {!resolved && canHold && (
            <div className="text-[11px] text-neutral-300 mb-1">
              <span className="text-[#a78bfa] font-bold uppercase tracking-widest text-[9px] mr-1">Choose:</span>
              Pay 1 token <span className="text-neutral-600">or</span> sacrifice your lowest-DEF Fighter?
            </div>
          )}

          {!resolved && !canHold && (
            <div className="text-[11px] text-red-300 mb-1">
              No tokens left · no Fighters to sacrifice · Lifeless Portrait self-destroys.
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={pay}
              disabled={!canPay || resolved}
              data-testid="upkeep-pay-btn"
              className="bg-[#FCD34D] hover:bg-[#FBBF24] disabled:bg-neutral-800 disabled:text-neutral-600 text-black font-bold uppercase tracking-widest text-[11px] py-2 transition"
            >
              Pay 1 Token
            </button>
            <button
              onClick={sacrifice}
              disabled={!canSacrifice || resolved}
              data-testid="upkeep-sacrifice-btn"
              className="bg-red-700 hover:bg-red-600 disabled:bg-neutral-800 disabled:text-neutral-600 text-white font-bold uppercase tracking-widest text-[11px] py-2 transition"
            >
              {lowest ? `Sacrifice ${lowest.name}` : "Sacrifice —"}
            </button>
          </div>

          {!resolved && !canHold && (
            <button
              onClick={autoDestroy}
              data-testid="upkeep-destroy-btn"
              className="w-full bg-zinc-900 hover:bg-zinc-800 border border-red-700 text-red-300 font-bold uppercase tracking-widest text-[11px] py-2 transition"
            >
              Destroy Lifeless Portrait
            </button>
          )}

          {resolved && (
            <button
              onClick={reset}
              data-testid="upkeep-reset-btn"
              className="w-full bg-[#7c3aed] hover:bg-[#8b5cf6] text-white font-bold uppercase tracking-widest text-[11px] py-2 transition"
            >
              Replay Upkeep Phase
            </button>
          )}

          {!resolved && canHold && turn > 1 && (
            <button
              onClick={nextUpkeep}
              data-testid="upkeep-next-btn"
              className="w-full bg-zinc-900 hover:bg-zinc-800 text-neutral-400 uppercase tracking-widest text-[10px] py-1.5 transition border border-zinc-800"
            >
              Advance one more upkeep tick
            </button>
          )}
        </div>

        <button
          onClick={onClose}
          data-testid="upkeep-close-btn"
          className="w-full bg-zinc-900 hover:bg-zinc-800 text-neutral-400 py-2 uppercase tracking-widest text-xs border-t border-zinc-800"
        >
          Close
        </button>
      </div>
    </div>
  );
}
